import dayjs from 'dayjs';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Typography,
  Checkbox,
} from '@mui/material';
import {DialogClose} from '../../components/dialogs/DialogFrame/DialogClose';
import {
  ISharedFacilityVisibilityPayload,
  IFacilityShareWindowDraft,
} from '../../dto/FacilitySharing.dto';
import {useRqChildCompanies} from '../../react_query/messages/sharing';
import {
  useRqSharedFacilityVisibility,
  useRqSharedFacilityVisibilityDetail,
} from '../../react_query/facilitySharing/useRqSharedFacilityVisibility';
import Toast from '../../package/Toaster';

interface SharedFacilityVisibilityDialogProps {
  open: boolean;
  onClose: () => void;
  companyId: number;
  stationId: number;
  stationName: string;
}

const toDateTimeLocalValue = (value: dayjs.ConfigType): string => {
  return dayjs(value).format('YYYY-MM-DDTHH:mm');
};

const getDefaultWindowDraft = (): IFacilityShareWindowDraft => {
  const startsAt = dayjs().add(15, 'minute').second(0).millisecond(0);

  return {
    starts_at: toDateTimeLocalValue(startsAt),
    ends_at: toDateTimeLocalValue(startsAt.add(4, 'hour')),
  };
};

const SharedFacilityVisibilityDialog: React.FC<
  SharedFacilityVisibilityDialogProps
> = ({open, onClose, companyId, stationId, stationName}) => {
  const [selectedCompanyIds, setSelectedCompanyIds] = useState<number[]>([]);
  const [targetWindows, setTargetWindows] = useState<
    Record<number, IFacilityShareWindowDraft>
  >({});

  const {data: childCompanies, isLoading: isLoadingChildCompanies} =
    useRqChildCompanies(companyId, open);
  const {data: visibilityDetail, isLoading: isLoadingVisibility} =
    useRqSharedFacilityVisibilityDetail(companyId, stationId, open);
  const {saveMutation, disableMutation} = useRqSharedFacilityVisibility(
    companyId,
    stationId,
  );

  const activeChildCompanies = useMemo(() => {
    return (childCompanies || []).filter(company => company.is_active);
  }, [childCompanies]);

  const selectedCompanyIdSet = useMemo(
    () => new Set(selectedCompanyIds),
    [selectedCompanyIds],
  );

  const hasActiveShare = Boolean(visibilityDetail?.data.length);

  useEffect(() => {
    if (!open) {
      return;
    }

    const targets = visibilityDetail?.data || [];

    if (targets.length > 0) {
      setSelectedCompanyIds(
        Array.from(
          new Set(targets.map(target => target.target_company_id)),
        ),
      );
      setTargetWindows(
        targets.reduce<Record<number, IFacilityShareWindowDraft>>(
          (acc, target) => {
            acc[target.target_company_id] = {
              starts_at: toDateTimeLocalValue(target.starts_at || dayjs()),
              ends_at: toDateTimeLocalValue(target.ends_at || dayjs().add(4, 'hour')),
            };

            return acc;
          },
          {},
        ),
      );

      return;
    }

    if (!isLoadingVisibility) {
      setSelectedCompanyIds([]);
      setTargetWindows({});
    }
  }, [isLoadingVisibility, open, visibilityDetail]);

  useEffect(() => {
    if (!open) {
      return;
    }

    setTargetWindows(prev => {
      const nextWindows: Record<number, IFacilityShareWindowDraft> = {};

      selectedCompanyIds.forEach(companyTargetId => {
        nextWindows[companyTargetId] =
          prev[companyTargetId] || getDefaultWindowDraft();
      });

      return nextWindows;
    });
  }, [open, selectedCompanyIds]);

  const handleCompanyToggle = useCallback((targetCompanyId: number) => {
    setSelectedCompanyIds(prev => {
      if (prev.includes(targetCompanyId)) {
        return prev.filter(id => id !== targetCompanyId);
      }

      return [...prev, targetCompanyId];
    });
  }, []);

  const handleWindowChange = useCallback(
    (
      targetCompanyId: number,
      field: keyof IFacilityShareWindowDraft,
      value: string,
    ) => {
      setTargetWindows(prev => ({
        ...prev,
        [targetCompanyId]: {
          ...(prev[targetCompanyId] || getDefaultWindowDraft()),
          [field]: value,
        },
      }));
    },
    [],
  );

  const invalidCompanyIds = useMemo(() => {
    return selectedCompanyIds.filter(targetCompanyId => {
      const window = targetWindows[targetCompanyId];

      if (!window?.starts_at || !window?.ends_at) {
        return true;
      }

      return dayjs(window.ends_at).isSameOrBefore(dayjs(window.starts_at));
    });
  }, [selectedCompanyIds, targetWindows]);

  const canSave =
    selectedCompanyIds.length > 0 && invalidCompanyIds.length === 0;

  const handleSave = useCallback(() => {
    if (!canSave) {
      return;
    }

    const payload: ISharedFacilityVisibilityPayload = {
      targets: selectedCompanyIds.map(targetCompanyId => {
        const companyWindow =
          targetWindows[targetCompanyId] || getDefaultWindowDraft();

        return {
          target_company_id: targetCompanyId,
          starts_at: dayjs(companyWindow.starts_at).toISOString(),
          ends_at: dayjs(companyWindow.ends_at).toISOString(),
        };
      }),
    };

    saveMutation.mutate(payload, {
      onSuccess: () => {
        Toast('Shared facility visibility updated successfully', 'success');
        onClose();
      },
    });
  }, [canSave, onClose, saveMutation, selectedCompanyIds, targetWindows]);

  const handleDisableVisibility = useCallback(() => {
    disableMutation.mutate(undefined, {
      onSuccess: () => {
        Toast('Shared facility visibility disabled', 'success');
        onClose();
      },
    });
  }, [disableMutation, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='md'>
      <Stack
        direction='row'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        pt={2}
        pb={1}
        sx={{borderBottom: '1px solid', borderColor: 'divider'}}>
        <Box>
          <Typography variant='h6'>Manage Shared Facility Visibility</Typography>
          <Typography variant='body2' color='text.secondary'>
            {stationName}
          </Typography>
        </Box>
        <DialogClose onClose={onClose} />
      </Stack>

      <DialogContent>
        <Stack spacing={2.5} mt={1}>
          <Alert severity='info'>
            Select the child companies that should be able to use this shared
            facility station, then configure the active visibility window for
            each one.
          </Alert>

          {hasActiveShare ? (
            <Alert severity='success'>
              This shared facility currently has active visibility targets.
            </Alert>
          ) : null}

          <Box>
            <Typography variant='subtitle2' sx={{mb: 1}}>
              Child Companies
            </Typography>
            {isLoadingChildCompanies ? (
              <Box>
                <Skeleton height={42} />
                <Skeleton height={42} />
                <Skeleton height={42} />
              </Box>
            ) : activeChildCompanies.length > 0 ? (
              <List
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  maxHeight: 240,
                  overflow: 'auto',
                }}>
                {activeChildCompanies.map(company => {
                  const isSelected = selectedCompanyIdSet.has(company.id);

                  return (
                    <ListItemButton
                      key={company.id}
                      selected={isSelected}
                      onClick={() => handleCompanyToggle(company.id)}
                      divider>
                      <Checkbox checked={isSelected} disableRipple tabIndex={-1} />
                      <ListItemText primary={company.name} />
                    </ListItemButton>
                  );
                })}
              </List>
            ) : (
              <Typography color='text.secondary'>
                No active child companies are available.
              </Typography>
            )}
          </Box>

          {isLoadingVisibility ? (
            <Box>
              <Skeleton height={96} />
              <Skeleton height={96} />
            </Box>
          ) : selectedCompanyIds.length > 0 ? (
            <Stack spacing={2}>
              {selectedCompanyIds.map(targetCompanyId => {
                const company = activeChildCompanies.find(
                  item => item.id === targetCompanyId,
                );
                const window =
                  targetWindows[targetCompanyId] || getDefaultWindowDraft();
                const hasError = invalidCompanyIds.includes(targetCompanyId);

                return (
                  <Box
                    key={targetCompanyId}
                    sx={{
                      p: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2,
                    }}>
                    <Typography variant='subtitle2' sx={{mb: 1.5}}>
                      {company?.name || `Company #${targetCompanyId}`}
                    </Typography>
                    <Stack direction={{xs: 'column', md: 'row'}} spacing={2}>
                      <TextField
                        fullWidth
                        type='datetime-local'
                        label='Starts At'
                        value={window.starts_at}
                        onChange={event =>
                          handleWindowChange(
                            targetCompanyId,
                            'starts_at',
                            event.target.value,
                          )
                        }
                        InputLabelProps={{shrink: true}}
                      />
                      <TextField
                        fullWidth
                        type='datetime-local'
                        label='Ends At'
                        value={window.ends_at}
                        onChange={event =>
                          handleWindowChange(
                            targetCompanyId,
                            'ends_at',
                            event.target.value,
                          )
                        }
                        InputLabelProps={{shrink: true}}
                        error={hasError}
                        helperText={
                          hasError
                            ? 'End time must be after the start time.'
                            : ' '
                        }
                      />
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              Choose one or more child companies to configure shared facility
              visibility.
            </Typography>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{px: 3, pb: 3}}>
        {hasActiveShare ? (
          <Button
            variant='outlined'
            color='error'
            onClick={handleDisableVisibility}
            disabled={disableMutation.isLoading}
            sx={{mr: 'auto'}}>
            {disableMutation.isLoading ? 'Disabling...' : 'Disable Visibility'}
          </Button>
        ) : null}
        <Button variant='outlined' onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!canSave || saveMutation.isLoading}>
          {saveMutation.isLoading ? (
            <CircularProgress size={20} color='inherit' />
          ) : (
            'Save Visibility'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SharedFacilityVisibilityDialog;
